const STOPS = [
  [0.1, [120, 220, 120, 0.45]], [0.5, [40, 190, 70, 0.6]], [1, [10, 150, 40, 0.7]], [2, [250, 230, 40, 0.75]],
  [4, [250, 160, 20, 0.8]], [8, [240, 60, 30, 0.85]], [16, [200, 0, 80, 0.9]], [30, [190, 60, 230, 0.95]],
];
export const LEGEND = STOPS.map(([v, c]) => [v, `rgb(${c[0]},${c[1]},${c[2]})`]);
export const COLS = 18, ROWS = 12, SUB = 4;

function colorOf(v) {
  if (v < 0.03) return null;
  if (v < STOPS[0][0]) { const c = STOPS[0][1]; return [c[0], c[1], c[2], (c[3] * (v - 0.03)) / (STOPS[0][0] - 0.03)]; }
  for (let i = 1; i < STOPS.length; i += 1) {
    if (v < STOPS[i][0]) {
      const [v0, c0] = STOPS[i - 1], [v1, c1] = STOPS[i];
      const k = (v - v0) / (v1 - v0);
      return c0.map((x, j) => x + (c1[j] - x) * k);
    }
  }
  return STOPS[STOPS.length - 1][1];
}

const bilinear = (values, rows, cols, fy, fx) => {
  const i = Math.min(rows - 2, Math.floor(fy)), j = Math.min(cols - 2, Math.floor(fx)), dy = fy - i, dx = fx - j;
  const a = values[i * cols + j], b = values[i * cols + j + 1], c = values[(i + 1) * cols + j], d = values[(i + 1) * cols + j + 1];
  return a * (1 - dx) * (1 - dy) + b * dx * (1 - dy) + c * (1 - dx) * dy + d * dx * dy;
};

export function renderField(values, rows = ROWS, cols = COLS, w = 432, h = 288) {
  const src = document.createElement("canvas");
  src.width = w;
  src.height = h;
  const sctx = src.getContext("2d");
  const img = sctx.createImageData(w, h);
  for (let y = 0; y < h; y += 1) {
    const fy = (y / (h - 1)) * (rows - 1);
    for (let x = 0; x < w; x += 1) {
      const c = colorOf(bilinear(values, rows, cols, fy, (x / (w - 1)) * (cols - 1)));
      if (!c) continue;
      const o = (y * w + x) * 4;
      img.data[o] = c[0]; img.data[o + 1] = c[1]; img.data[o + 2] = c[2]; img.data[o + 3] = Math.round(c[3] * 255);
    }
  }
  sctx.putImageData(img, 0, 0);
  const out = document.createElement("canvas");
  out.width = w;
  out.height = h;
  const octx = out.getContext("2d");
  octx.filter = "blur(3px)";
  octx.drawImage(src, 0, 0);
  return out.toDataURL();
}

export function valueAt(grid, values, lat, lon) {
  const [[s, w], [n, e]] = grid.bounds;
  if (lat < s || lat > n || lon < w || lon > e) return null;
  return bilinear(values, grid.rows, grid.cols, ((n - lat) / (n - s)) * (grid.rows - 1), ((lon - w) / (e - w)) * (grid.cols - 1));
}

export function tweenFrames(keys) {
  const out = [];
  keys.forEach((k, idx) => {
    out.push({ t: k.t, v: k.v, key: idx });
    const nx = keys[idx + 1];
    if (!nx) return;
    for (let s = 1; s < SUB; s += 1) {
      const r = s / SUB;
      out.push({ t: k.t + (nx.t - k.t) * r, v: k.v.map((x, i) => x + (nx.v[i] - x) * r) });
    }
  });
  return out;
}

export async function fetchRadarFrames() {
  const r = await fetch("https://api.rainviewer.com/public/weather-maps.json");
  if (!r.ok) throw new Error(`rainviewer ${r.status}`);
  const d = await r.json();
  return (d.radar?.past || []).map((p) => ({ t: p.time, url: `${d.host}${p.path}/256/{z}/{x}/{y}/2/1_0.png` }));
}

export async function fetchForecastGrid(b) {
  const n = b.getNorth(), s = b.getSouth(), w = b.getWest(), e = b.getEast();
  const lats = Array.from({ length: ROWS }, (_, i) => +(n - ((n - s) * i) / (ROWS - 1)).toFixed(4));
  const lons = Array.from({ length: COLS }, (_, j) => +(w + ((e - w) * j) / (COLS - 1)).toFixed(4));
  const pts = lats.flatMap((la) => lons.map((lo) => [la, lo]));
  const qs = new URLSearchParams({ latitude: pts.map((p) => p[0]).join(","), longitude: pts.map((p) => p[1]).join(","),
    minutely_15: "precipitation", forecast_minutely_15: "5", timezone: "GMT", timeformat: "unixtime" });
  const r = await fetch(`https://api.open-meteo.com/v1/forecast?${qs}`);
  if (!r.ok) throw new Error(`open-meteo ${r.status}`);
  const rows = await r.json();
  const keys = rows[0].minutely_15.time.map((t, k) => ({ t, v: rows.map((row) => (row.minutely_15.precipitation[k] || 0) * 4) }));
  return { source: "openmeteo", rows: ROWS, cols: COLS, bounds: [[s, w], [n, e]], keys, fetched_at: Date.now() / 1000 };
}
